import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { MarketingCampaign } from "@/types/sms";

/**
 * The Order Invite batch history: the `play='invite'` marketing_campaigns rows,
 * newest first — one per store-local day. RLS scopes the read to the caller's
 * store. Read-only; the columns (recipients_count/sent_count/... /audience_filter)
 * are all already written by the invite_rotation worker.
 */
export const useInviteBatches = (limit = 14) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<MarketingCampaign[]>({
    queryKey: ["sms-invite-batches", limit],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("marketing_campaigns")
        .select("*")
        .eq("play", "invite")
        .order("invite_occurrence_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as MarketingCampaign[];
    },
    staleTime: 60 * 1000,
  });
};
